import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import { formatDay } from '@lensmira/i18n'

import { Ltr, useTranslation } from '../i18n.js'
import { DataSurface } from './DataSurface.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'

/**
 * Closing a month. §6.11 `ACC-008`; Appendix B's `Period - close`, owner and accountant.
 *
 * > `ACC-008` `MUST` Period close: lock a period so historical figures cannot change. Post-close
 * > corrections go to the current period as adjustments.
 *
 * ---
 *
 * **One button and a sentence that cannot be taken back.** There is no reopen in `ACC-008` and
 * there is no route for one, so this screen asks before it writes and says so in the asking. The
 * confirmation names the month, because "are you sure" asks nothing.
 *
 * **It offers the months that can be closed rather than a date field.** A period is `YYYY-MM` and
 * the server refuses anything else, refuses a month still being traded in, and refuses one already
 * closed. A text box would make a shopkeeper discover all three by being told no, so the chooser
 * only holds months that are over and not yet closed.
 *
 * **What is already closed is on screen beside it**, because the question a shopkeeper actually
 * has is "did we do September", and a screen that only offers the next action cannot answer it.
 */

export interface ClosedPeriodData {
  readonly period: string
  readonly closedAt: string
  readonly closedByStaffId: string
  readonly reason: string | null
}

export interface PeriodCloseProps {
  readonly loadClosed: () => Promise<readonly ClosedPeriodData[]>
  /** Appendix B's `period_close`. The routes check the same row. */
  readonly canClose: boolean
  /** The branch's own today, so the months offered are the ones that have actually ended. */
  readonly today: string
  readonly timezone: string
  readonly onClosed?: () => void
}

/** The twelve months before `today`, newest first. `ACC-008` locks historical figures only. */
function closableMonths(today: string): readonly string[] {
  const year = Number(today.slice(0, 4))
  const month = Number(today.slice(5, 7))
  if (!Number.isInteger(year) || !Number.isInteger(month)) return []
  const out: string[] = []
  for (let back = 1; back <= 12; back += 1) {
    const m = month - back
    const y = year + Math.floor((m - 1) / 12)
    const normalised = ((((m - 1) % 12) + 12) % 12) + 1
    out.push(`${String(y).padStart(4, '0')}-${String(normalised).padStart(2, '0')}`)
  }
  return out
}

export function PeriodClose({
  loadClosed,
  canClose,
  today,
  timezone,
  onClosed,
}: PeriodCloseProps): JSX.Element | null {
  const { t, locale } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [closed, setClosed] = useState<readonly ClosedPeriodData[] | null>(null)
  const [failed, setFailed] = useState(false)
  const [period, setPeriod] = useState('')
  const [reason, setReason] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [writeId, setWriteId] = useState<string | null>(null)

  const refresh = useCallback(() => {
    let cancelled = false
    setFailed(false)
    void loadClosed()
      .then((rows) => {
        if (!cancelled) setClosed(rows)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [loadClosed])

  useEffect(() => refresh(), [refresh])

  useWriteSettled(snapshot, writeId, () => {
    setWriteId(null)
    setConfirming(false)
    setPeriod('')
    setReason('')
    refresh()
    onClosed?.()
  })

  const close = useCallback(async () => {
    const write = (await enqueue({
      method: 'POST',
      path: `/periods/${encodeURIComponent(period)}/close`,
      label: t('periodClose.queueLabel', { period }),
      body: reason.trim() === '' ? {} : { reason: reason.trim() },
    })) as { id: string }
    setWriteId(write.id)
  }, [enqueue, period, reason, t])

  if (!canClose) return null

  const alreadyClosed = new Set((closed ?? []).map((row) => row.period))
  const choices = closableMonths(today).filter((month) => !alreadyClosed.has(month))
  const line = statusLine(t, snapshot, writeId, t('queue.subject.periodClose'))

  return (
    <section className="lm-screen" aria-labelledby="period-close-heading">
      <h1 id="period-close-heading" className="lm-screen__heading">
        {t('periodClose.heading')}
      </h1>
      <p className="lm-hint">{t('periodClose.whatItIs')}</p>

      <h2 className="lm-screen__subheading">{t('periodClose.alreadyHeading')}</h2>
      <DataSurface
        state={
          failed
            ? {
                kind: 'error',
                message: t('periodClose.loadFailed'),
                onRetry: () => {
                  refresh()
                },
              }
            : closed === null
              ? { kind: 'loading' }
              : closed.length === 0
                ? { kind: 'empty' }
                : { kind: 'ready' }
        }
        shape="rows"
        rows={3}
        what={t('periodClose.what')}
        emptyMessage={t('periodClose.noneClosedInvite')}
      >
        <ul className="lm-period-list">
          {(closed ?? []).map((row) => (
            <li key={row.period} className="lm-period">
              <span className="lm-period__month" dir="ltr">
                <Ltr>{row.period}</Ltr>
              </span>
              <span className="lm-period__when">
                {t('periodClose.closedOn', { date: formatDay(row.closedAt, timezone, locale) })}
              </span>
              {row.reason === null ? null : (
                <span className="lm-period__reason" dir="auto">
                  {row.reason}
                </span>
              )}
            </li>
          ))}
        </ul>
      </DataSurface>

      {/*
        Withheld until the closed list has arrived. The chooser is built from what is not yet closed,
        so offered while that list is loading or failed it would put months on screen that may
        already be closed, which is what the load-failure sentence promises it does not do.
      */}
      {closed === null || failed ? null : (
        <h2 className="lm-screen__subheading">{t('periodClose.closeHeading')}</h2>
      )}
      {closed === null || failed ? null : choices.length === 0 ? (
        <p className="lm-empty">{t('periodClose.nothingToClose')}</p>
      ) : (
        <>
          <label className="lm-field">
            <span className="lm-field__label">{t('periodClose.monthLabel')}</span>
            <select
              className="lm-input"
              value={period}
              onChange={(e) => {
                setPeriod(e.currentTarget.value)
                setConfirming(false)
              }}
            >
              <option value="">{t('periodClose.monthUnchosen')}</option>
              {choices.map((month) => (
                <option key={month} value={month}>
                  {month}
                </option>
              ))}
            </select>
          </label>

          <label className="lm-field">
            <span className="lm-field__label">{t('periodClose.reasonLabel')}</span>
            <input
              className="lm-input"
              dir="auto"
              value={reason}
              autoComplete="off"
              onChange={(e) => setReason(e.currentTarget.value)}
            />
          </label>

          {line === null ? null : (
            <p className="lm-queue-state" role="status">
              {line}
            </p>
          )}

          {confirming ? (
            <>
              {/* Names the month. "Are you sure" asks nothing, and there is no way back. */}
              <p className="lm-field__warning">{t('periodClose.confirmMeans', { period })}</p>
              <button
                type="button"
                className="lm-button lm-button--danger"
                disabled={writeId !== null}
                onClick={() => {
                  void close()
                }}
              >
                {t('periodClose.confirm', { period })}
              </button>
              <button
                type="button"
                className="lm-button lm-button--secondary"
                onClick={() => {
                  setConfirming(false)
                }}
              >
                {t('periodClose.cancel')}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="lm-button lm-button--primary"
              disabled={period === ''}
              onClick={() => {
                setConfirming(true)
              }}
            >
              {t('periodClose.start')}
            </button>
          )}
        </>
      )}
    </section>
  )
}
